import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Send, MessageSquare, MessageCircle } from 'lucide-react';
import { useSelector } from 'react-redux';
import { addCommentsMessage } from '../../services/operations/interviewExperiences';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
// Fallback logo component
const TakeUforwardLogo = ({ className = "w-full h-full object-cover" }) => {
  return (
    <div className={`bg-orange-500 rounded-full flex items-center justify-center text-white font-bold text-xs ${className}`}>
      TUF
    </div>
  );
};


const InterviewExperienceChat = () => {
  const { user } = useSelector((state) => state.auth || { user: { name: 'Admin User' } });
  const [showChat, setShowChat] = useState(true);
  const { InterviewExperienceChat } = useSelector((state) => state.interviewExperience);
  const dispatch = useDispatch();
  const { id } = useParams();
  // Chat state with dummy data
  const [messages, setMessages] = useState(InterviewExperienceChat);
  const [newMessage, setNewMessage] = useState('');
  // eslint-disable-next-line
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (InterviewExperienceChat) {
      setMessages(InterviewExperienceChat);
    }
  }, [InterviewExperienceChat]);

  // Auto scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || isLoading) return;

    const message = {
      id: Date.now(),
      message: newMessage.trim(),
      sender: 'takeUforward',
      senderName: user?.name || 'takeUforward Admin',
      timestamp: new Date(),
      isResolved: false,
      role: 'Content Reviewer'
    };

    const updatedMessages = [...messages, message];

    // Add message to state (simulating API call)
    setMessages(updatedMessages);
    dispatch(addCommentsMessage(id, updatedMessages));
    setNewMessage('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleMarkAsResolved = async (messageId) => {
    const updatedMessages = messages.map((msg) =>
      msg.id === messageId ? { ...msg, isResolved: !msg.isResolved } : msg,
    );
    setMessages(updatedMessages);
    dispatch(addCommentsMessage(id, updatedMessages));
  };

  const formatTime = (timestamp) => {
    const messageTime = new Date(timestamp);
    return messageTime
      .toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })
      .toLowerCase();
  };

  const NotResolvedMessageOfAdmin = useMemo(() => {
    return messages.filter((message) => !message.isResolved);
  }, [messages]);

  return (
    <>
      {/* Unresolved User Comment Notifications - Show when chat is closed */}
      {!showChat && NotResolvedMessageOfAdmin.length > 0 && (
        <div className='fixed bottom-24 right-6 z-20 space-y-3 max-w-xs'>
          {NotResolvedMessageOfAdmin.slice(0, 5).map((message, index) => (
            <div
              key={message.id}
              className={`bg-white dark:bg-[#18191B] border rounded-lg shadow-lg p-3 cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:scale-105 ${
                message.sender === 'user' 
                  ? 'border-blue-200 dark:border-blue-800' 
                  : 'border-orange-200 dark:border-orange-800'
              }`}
              onClick={() => setShowChat(true)}
            >
              <div className='flex items-start gap-2'>
                <div className='w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0'>
                  {message.sender === 'user' ? (
                    <div className='w-full h-full bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold'>
                      U
                    </div>
                  ) : (
                    <TakeUforwardLogo className='w-full h-full object-cover' />
                  )}
                </div>
                <div className='flex-1 min-w-0'>
                  <div className='flex items-center gap-2 mb-1'>
                    <span className='text-xs font-medium text-gray-900 dark:text-[#FAFAFA]'>
                      {message.sender === 'user' ? 'User' : 'takeUforward Admin'}
                    </span>
                    <div className={`w-1.5 h-1.5 rounded-full ${
                      message.sender === 'user' ? 'bg-blue-500' : 'bg-orange-500'
                    }`}></div>
                  </div>
                  <p className='text-xs text-gray-600 dark:text-gray-300 line-clamp-2 leading-relaxed'>
                    {message.message}
                  </p>
                  <div className='text-[10px] text-gray-400 dark:text-gray-500 mt-1'>
                    {formatTime(message.timestamp)}
                  </div>
                </div>
              </div>
            </div>
          ))}
          {NotResolvedMessageOfAdmin.length > 5 && (
            <div
              className='bg-gray-50 dark:bg-gray-900/20 border border-gray-200 dark:border-gray-800 rounded-lg p-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-900/30 transition-colors'
              onClick={() => setShowChat(true)}
            >
              <p className='text-xs text-gray-600 dark:text-gray-400 text-center font-medium'>
                +{NotResolvedMessageOfAdmin.length - 5} more unresolved comments
              </p>
            </div>
          )}
        </div>
      )}

      {/* Mobile Comments Button - FAB for mobile screens */}
      {!showChat && (
        <div className='fixed bottom-6 right-6 z-30 lg:hidden'>
          <button
            onClick={() => setShowChat(true)}
            className='relative w-14 h-14 bg-orange-500 hover:bg-orange-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 ease-in-out transform hover:scale-110 active:scale-95 flex items-center justify-center'
          >
            <MessageCircle className='w-6 h-6' />

            {/* Notification Badge for Mobile FAB */}
            {NotResolvedMessageOfAdmin.length > 0 && (
              <div className='absolute -top-2 -right-2 min-w-[20px] h-[20px] bg-red-500 rounded-full flex items-center justify-center border-2 border-white shadow-lg'>
                <span className='text-[10px] text-white font-bold leading-none'>
                  {NotResolvedMessageOfAdmin.length}
                </span>
              </div>
            )}
          </button>
        </div>
      )}

      {/* Desktop Comments Button - Enhanced Design */}
      {!showChat && (
        <div className='hidden lg:block fixed bottom-6 right-6 z-30'>
          <button
            onClick={() => setShowChat(true)}
            className='relative flex items-center gap-3 pl-4 pr-3 py-3 rounded-full transition-all duration-300 ease-in-out bg-gradient-to-r from-gray-800 to-gray-700 dark:from-gray-700 dark:to-gray-600 hover:from-gray-700 hover:to-gray-600 dark:hover:from-gray-600 dark:hover:to-gray-500 text-white shadow-xl hover:shadow-2xl transform hover:scale-105 active:scale-95 border border-gray-600/20 backdrop-blur-sm'
          >
            {/* Comments Label Section */}
            <div className='flex items-center gap-2.5'>
              <div className='p-1.5 bg-white/10 rounded-full'>
                <MessageCircle className='w-4 h-4' />
              </div>
              <span className='text-sm font-semibold tracking-wide'>
                {messages.length > 0 ? 'Comments' : 'Feedback'}
              </span>
            </div>

            {/* Avatars and Count Section */}
            <div className='flex items-center gap-2 pl-2 border-l border-white/20'>
              {/* Avatar Stack */}
              <div className='flex -space-x-1.5'>
                {/* takeUforward Avatar */}
                <div
                  className='relative w-7 h-7 rounded-full border-2 border-white/80 shadow-lg overflow-hidden bg-white'
                  style={{ zIndex: 3 }}
                >
                  <TakeUforwardLogo className='w-full h-full object-cover' />
                </div>

                {/* User Avatar */}
                <div
                  className='relative w-7 h-7 rounded-full border-2 border-white/80 shadow-lg overflow-hidden bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center'
                  style={{ zIndex: 2 }}
                >
                  <span className='text-white text-xs font-bold'>U</span>
                </div>
              </div>

              {/* Count Badge */}
              {NotResolvedMessageOfAdmin.length > 0 && (
                <div className='relative'>
                  <div className='min-w-[24px] h-6 bg-gradient-to-r from-red-500 to-red-600 rounded-full flex items-center justify-center shadow-lg border-2 border-white/80'>
                    <span className='text-white text-xs font-bold px-1.5'>
                      {NotResolvedMessageOfAdmin.length}
                    </span>
                  </div>
                  {/* Subtle glow effect */}
                  <div className='absolute inset-0 bg-red-500 rounded-full blur-sm opacity-30 -z-10'></div>
                </div>
              )}
            </div>

            {/* Subtle shine effect */}
            <div className='absolute inset-0 rounded-full bg-gradient-to-r from-transparent via-white/5 to-transparent pointer-events-none'></div>
          </button>
        </div>
      )}

      {/* Chat Section - Overlay on Mobile, Sidebar on Desktop */}
      {showChat && (
        <>
          {/* Mobile Overlay Background */}
          <div
            className='fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden'
            onClick={() => setShowChat(false)}
          />

          {/* Chat Container */}
          <div
            className={`
            ${/* Mobile: Fixed overlay */ ''}
            fixed lg:relative
            inset-x-0 bottom-0 lg:inset-auto
            w-full lg:w-[400px]
            h-[85vh] lg:h-full
            z-50 lg:z-auto
            lg:shadow-xl
            rounded-t-2xl lg:rounded-xl
            flex-shrink-0
            transform transition-transform duration-300 ease-in-out
            ${showChat ? 'translate-y-0' : 'translate-y-full lg:translate-y-0'}
          `}
          >
            <ChatBox
              messages={messages}
              newMessage={newMessage}
              setNewMessage={setNewMessage}
              onSendMessage={handleSendMessage}
              onKeyPress={handleKeyPress}
              isLoading={isLoading}
              messagesEndRef={messagesEndRef}
              formatTime={formatTime}
              currentUser={user}
              onClose={() => setShowChat(false)}
              onMarkAsResolved={handleMarkAsResolved}
              unresolvedMessages={NotResolvedMessageOfAdmin}
            />
          </div>
        </>
      )}
    </>
  );
};

// Chat Box Component
const ChatBox = ({
  messages,
  newMessage,
  setNewMessage,
  onSendMessage,
  onKeyPress,
  isLoading,
  messagesEndRef,
  formatTime,
  currentUser,
  onClose,
  onMarkAsResolved,
  unresolvedMessages,
}) => {
  return (
    <div className='flex flex-col h-full bg-white dark:bg-[#18191B] rounded-t-2xl lg:rounded-xl shadow-lg'>
      {/* Chat Header */}
      <div className='px-4 sm:px-6 py-3 border-b border-gray-100 dark:border-gray-600 bg-white dark:bg-[#18191B] rounded-t-2xl lg:rounded-t-xl relative'>
        {/* Mobile Close Handle */}
        <div className='w-12 h-1 bg-gray-300 dark:bg-gray-600 rounded-full mx-auto mb-3 lg:hidden' />

        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-3'>
            <h3 className='text-base sm:text-lg font-semibold text-gray-900 dark:text-[#FAFAFA]'>
              {messages.length > 0 ? 'Comments' : 'Feedback & Comments'}
            </h3>
            {/* Unresolved Comments Count */}
            {unresolvedMessages.length > 0 && (
              <div className='flex items-center gap-1.5 px-2.5 py-1 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-full'>
                <div className='w-2 h-2 bg-orange-500 rounded-full'></div>
                <span className='text-xs font-medium text-orange-600 dark:text-orange-400'>
                  {unresolvedMessages.length} unresolved
                </span>
              </div>
            )}
          </div>

          {/* Desktop Close Button */}
          <button
            onClick={onClose}
            className='hidden lg:flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors'
          >
            <svg
              className='w-5 h-5 text-gray-500 dark:text-gray-400'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M6 18L18 6M6 6l12 12'
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Messages Container */}
      <div className='flex-1 overflow-y-auto scrollbar-hide px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 dark:bg-[#050505]'>
        <div className='space-y-4 sm:space-y-6'>
          {isLoading && messages.length === 0 ? (
            <div className='flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400 text-center'>
              <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mb-2'></div>
              <p className='text-sm'>Loading comments...</p>
            </div>
          ) : messages.length === 0 ? (
            <div className='flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400 text-center px-4'>
              <MessageSquare className='w-12 h-12 mb-4 opacity-50' />
              <h3 className='text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2'>
                Admin Review System
              </h3>
              <div className='space-y-2 text-sm max-w-md'>
                <p className='leading-relaxed'>
                  Use this space to provide feedback and suggestions to the user about their interview experience draft.
                </p>
                <p className='leading-relaxed'>
                  You can suggest improvements, ask for clarifications, and help ensure the experience meets quality standards.
                </p>
              </div>
            </div>
          ) : (
            messages.map((message) => (
              <MessageBubble
                key={message.id}
                message={message}
                formatTime={formatTime}
                isOwnMessage={message.sender === 'takeUforward'}
                onMarkAsResolved={onMarkAsResolved}
              />
            ))
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Message Input */}
      <div className='px-4 sm:px-6 py-3 sm:py-4 bg-white dark:bg-[#18191B] border-t border-gray-100 dark:border-gray-600 rounded-b-2xl lg:rounded-b-xl'>
        <div className='flex items-start space-x-2 sm:space-x-3'>
          <div className='flex-1'>
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={onKeyPress}
              placeholder='Type feedback for the user...'
              disabled={isLoading}
              className='w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 dark:border-gray-600 rounded-lg sm:rounded-xl resize-none bg-white dark:bg-[#050505] text-gray-900 dark:text-[#FAFAFA] placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:opacity-50 text-sm leading-relaxed'
              rows={1}
              style={{ minHeight: '40px' }}
            />
          </div>
          <button
            onClick={onSendMessage}
            disabled={!newMessage.trim() || isLoading}
            className='p-2 sm:p-3 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 dark:disabled:bg-gray-600 text-white rounded-lg sm:rounded-xl transition-colors disabled:cursor-not-allowed flex-shrink-0 shadow-sm'
          >
            <Send className='w-4 h-4' />
          </button>
        </div>
        <div className='mt-2 text-xs text-gray-400 dark:text-gray-500 hidden sm:block'>
          Press{' '}
          <kbd className='px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs font-mono'>
            Enter
          </kbd>{' '}
          to send,{' '}
          <kbd className='px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs font-mono'>
            Shift + Enter
          </kbd>{' '}
          for new line
        </div>
      </div>
    </div>
  );
};

// Message Bubble Component
const MessageBubble = ({ message, formatTime, isOwnMessage, onMarkAsResolved }) => {
  const istakeUforward = message.sender === 'takeUforward';

  return (
    <div>
      {/* User messages - left aligned */}
      {!istakeUforward && (
        <div className='flex items-start space-x-2 sm:space-x-3'>
          <div className='w-8 h-8 sm:w-10 sm:h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold text-xs sm:text-sm shadow-sm flex-shrink-0'>
            U
          </div>
          <div className='flex-1 min-w-0'>
            <div className='flex flex-col sm:flex-row sm:items-center space-y-0 sm:space-y-0 sm:space-x-2 mb-1'>
              <span className='font-semibold text-gray-900 dark:text-[#FAFAFA] text-sm'>
                User
              </span>
              {message.isResolved && (
                <span className='inline-flex items-center px-2 py-0.5 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full'>
                  ✓ Resolved
                </span>
              )}
            </div>
            <div
              className={`rounded-2xl rounded-tl-md px-3 sm:px-4 py-2 sm:py-3 shadow-sm border max-w-full ${
                message.isResolved
                  ? 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 opacity-75'
                  : 'bg-blue-500 border-blue-400'
              }`}
            >
              <p
                className={`text-sm leading-relaxed break-words ${
                  message.isResolved
                    ? 'text-gray-500 dark:text-gray-400'
                    : 'text-white'
                }`}
              >
                {message.message}
              </p>

              {/* Admin can resolve/unresolve user messages */}
              <div className='mt-3 pt-2 border-t border-blue-300/30'>
                <button
                  onClick={() => onMarkAsResolved(message.id)}
                  className={`inline-flex items-center px-3 py-1.5 text-xs rounded-lg transition-colors duration-200 font-medium ${
                    message.isResolved
                      ? 'bg-white/20 hover:bg-white/30 text-white border border-white/30'
                      : 'bg-white/20 hover:bg-white/30 text-white border border-white/30'
                  }`}
                >
                  <svg
                    className='w-3 h-3 mr-1'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d={message.isResolved ? 'M6 18L18 6M6 6l12 12' : 'M5 13l4 4L19 7'}
                    />
                  </svg>
                  {message.isResolved ? 'Mark as Unresolved' : 'Mark as Resolved'}
                </button>
              </div>
            </div>
            <div className='text-xs text-gray-400 dark:text-gray-500 mt-1 ml-1'>
              {formatTime(message.timestamp)}
            </div>
          </div>
        </div>
      )}

      {/* Admin messages (takeUforward) - right aligned */}
      {istakeUforward && (
        <div className='flex items-start justify-end space-x-2 sm:space-x-3'>
          <div className='flex-1 flex justify-end min-w-0'>
            <div className='max-w-[85%] sm:max-w-xs'>
              <div className='flex flex-col items-end'>
                {/* Admin name and status */}
                <div className='flex items-center gap-2 mb-1'>
                  {message.isResolved && (
                    <span className='inline-flex items-center px-2 py-0.5 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full'>
                      ✓ Resolved
                    </span>
                  )}
                  <span className='font-semibold text-gray-900 dark:text-[#FAFAFA] text-sm'>
                    takeUforward
                  </span>
                  <span className='text-xs text-gray-500 dark:text-gray-400'>{message.role}</span>
                </div>
                
                {/* Admin message bubble */}
                <div
                  className={`rounded-2xl rounded-tr-md px-3 sm:px-4 py-2 sm:py-3 shadow-sm border ${
                    message.isResolved
                      ? 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 opacity-75'
                      : 'bg-orange-500 border-orange-400'
                  }`}
                >
                  <p 
                    className={`text-sm leading-relaxed break-words ${
                      message.isResolved ? 'text-gray-500 dark:text-gray-400' : 'text-white'
                    }`}
                  >
                    {message.message}
                  </p>

                  {/* Admin can resolve/unresolve their own messages */}
                  <div className='mt-3 pt-2 border-t border-orange-300/30'>
                    <button
                      onClick={() => onMarkAsResolved(message.id)}
                      className={`inline-flex items-center px-3 py-1.5 text-xs rounded-lg transition-colors duration-200 font-medium ${
                        message.isResolved
                          ? 'bg-white/20 hover:bg-white/30 text-white border border-white/30'
                          : 'bg-white/20 hover:bg-white/30 text-white border border-white/30'
                      }`}
                    >
                      <svg
                        className='w-3 h-3 mr-1'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d={message.isResolved ? 'M6 18L18 6M6 6l12 12' : 'M5 13l4 4L19 7'}
                        />
                      </svg>
                      {message.isResolved ? 'Mark as Unresolved' : 'Mark as Resolved'}
                    </button>
                  </div>
                </div>
                
                <div className='text-xs text-gray-400 dark:text-gray-500 mt-1 text-right mr-1'>
                  {formatTime(message.timestamp)}
                </div>
              </div>
            </div>
          </div>
          <div className='w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white font-semibold text-xs sm:text-sm shadow-sm flex-shrink-0'>
            <TakeUforwardLogo className='w-full h-full object-cover' />
          </div>
        </div>
      )}
    </div>
  );
};

export default InterviewExperienceChat;
